import { State } from '@autopylot-internal/state';
import { Theme } from '../../app/ThemeVariables.ts';
import { AuthorizeMissionFacade, ISO8601DateTimeString, MissionFeatureCollection, MissionFeatureCollectionFacadeAdapter } from '@autopylot-internal/tiles-client';
import { MissionColorProvider } from '../../flight-checklist/MissionColorProvider.ts';
import { Feature, Polygon } from 'geojson';
import * as turf from '@turf/turf';
import { ICoordinate } from '../../support/model/ICoordinate.ts';
import { ApMapboxStylerTimeState } from '../styles/ApMapboxStylerTimeState.ts';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { APIClient } from '@autopylot-internal/autopylot-api-client';

export interface IRadiusProperties {
    center: ICoordinate;
    radiusMeters: number;
    color: string; // hex color
    startTime: string;
    endTime: string;
    isLicensed: boolean;
    authorizedRadius?: MissionFeatureCollection;
    isSquare?: boolean;
}

export class MissionPinAndRadiusOnMapViewModel extends State<Record<string, never>>{
    static inject = () => [
        Theme,
        AuthorizeMissionFacade,
        MissionFeatureCollectionFacadeAdapter,
        MissionColorProvider,
        ApMapboxStylerTimeState,
        TrackingService,
        APIClient
    ];
    constructor(
        private theme: Theme,
        private authorizeMissionProvider: AuthorizeMissionFacade,
        private missionAdapter: MissionFeatureCollectionFacadeAdapter,
        private missionColorProvider: MissionColorProvider,
        private timeState: ApMapboxStylerTimeState,
        private tracker: TrackingService,
        private apiClient: APIClient
    ){
        super({ });

        this.subscribe(() => {
            if (this.radius){
                this.timeState.setTime({
                    startTime: this.radius.properties.startTime,
                    endTime: this.radius.properties.endTime
                });
            }
            else {
                this.timeState.reset();
            }
        });
    }

    getRadius = (): Feature<Polygon, IRadiusProperties> | undefined => {
        return this.radius;
    };

    isSquare = () => {
        return this.radius?.properties.isSquare;
    };

    showPendingRadius = true;
    authorizeFailed = false;

    private radius?: Feature<Polygon, IRadiusProperties>;
    private panMovesRadius = false;
    getDoesPanMoveRadius = () => this.panMovesRadius;

    stopMovingMissionPin = () => {
        this.panMovesRadius = false;
        this.setState({ });
    };

    resumeMovingMissionPin = () => {
        this.panMovesRadius = true;
        this.setState({ });
    };

    start = async (props: {center: ICoordinate; radiusMeters: number; startTime: string; endTime: string; isLicensed: boolean;}) => {

        this.showPendingRadius = true;
        this.panMovesRadius = true;

        this.buildAndSetRadiusFromCenter(props);

        this.setState({ });

        await this.authorizeRadius();
    };

    toggleSquareOrCircle = () => {
        if (this.radius && this.panMovesRadius){
            this.buildAndSetRadiusFromCenter({
                ...this.radius.properties,
                isSquare: !this.radius.properties.isSquare
            });

            // TODO: error handling?
            this.authorizeRadius();

            this.tracker.track('Mission Shape Changed', () => ({
                missionShape: this.isSquare() ? 'square' : 'circle',
            }));
        }
    };

    showMissionPinAndRadius = (mission: MissionFeatureCollection) => {
        // if anything else was showing, remove it
        this.remove();

        const radius = mission.getMissionRadius();

        this.radius = {
            type: 'Feature',
            geometry: mission.getMissionRadius().geometry,
            properties: {
                ...radius.properties,
                color: this.theme.getColor(this.missionColorProvider.getColor(mission)),
                authorizedRadius: mission
            }
        };
        this.setState({ });
    };

    private buildAndSetRadiusFromCenter = (properties: Omit<IRadiusProperties, 'color'>) => {
        const { center, radiusMeters, isSquare } = properties;

        const { lng, lat } = center;
        let radius = turf.circle([lng, lat], radiusMeters, {
            units: 'meters',
            properties: {
                ...properties,
                color: this.theme.getColor('primary'),
                authorizedRadius: undefined
            }
        });

        if (isSquare){
            const bbox = turf.bbox(radius);
            radius = turf.bboxPolygon(bbox, { properties: radius.properties });
        }

        this.radius = radius;

        this.setState({ });
    };

    move = (center: ICoordinate) => {
        const metersMoved = this.getMetersMoved({ oldCenter: this.radius!.properties.center, newCenter: center });
        if (metersMoved < 1){ // eg a minor difference as camera zooms
            // then don't bother
            return;
        }

        this.showPendingRadius = false;

        this.buildAndSetRadiusFromCenter({
            ...this.radius!.properties,
            center
        });
    };

    private getMetersMoved = ({ oldCenter, newCenter }: { oldCenter: ICoordinate; newCenter: ICoordinate; }) => {
        const fromCoord = turf.point([oldCenter.lng, oldCenter.lat]);
        const toCoord = turf.point([newCenter.lng, newCenter.lat]);
        return turf.distance(fromCoord, toCoord, { units: 'meters'});
    };

    adjustRadiusSize = ({ radiusMeters }: { radiusMeters: number; }) => {
        this.showPendingRadius = true;

        this.buildAndSetRadiusFromCenter({
            ...this.radius!.properties,
            radiusMeters
        });
    };

    authorizeRadius = async () => {
        this.showPendingRadius = true;
        this.setState({});

        // important to save radius here, and use local variable radius below,
        // because this.getRadius() may change during the authorization process
        const radius = this.getRadius();

        if (!radius){
            throw new Error('no radius');
        }
        if (radius?.properties.authorizedRadius){
            // then it is already authorized
            return;
        }

        try{
            const persistable = await this.authorizeMissionProvider.subdivide({
                ...radius.properties,
                source: 'mission-radius'
            }, { initializedSource: 'user-mission-creation' });

            // TLDR: safe to use `radius`, not safe to use `this.getRadius()`
            // NOTE: at this point the radius at this.getRadius() may have changed
            // but we handle that by ignoring this.getRadius() and instead using the radius
            // we have clojure over above. By updating it, if it still the active one, the UI
            // will update, if it is not, the UI won't notice this out dated authorization response

            const authorizedRadius = this.missionAdapter.adapt(persistable);
            this.authorizeFailed = false;

            radius.properties.color = this.theme.getColor(this.missionColorProvider.getColor(authorizedRadius));
            radius.properties.authorizedRadius = authorizedRadius;

            this.setState({ });

            // get operational awareness
            const entry = await this.apiClient.entry();
            const beforeTimestamp = Date.now();
            this.apiClient.post(entry.links.operationalAwareness, radius.properties).then(operationalAwareness => {
                const afterTimestamp = Date.now();
                radius.properties.authorizedRadius.addOperationalAwareness(operationalAwareness);
                this.setState({ });
                this.tracker.track('Operational Awareness Computed', () => ({
                    center: radius.properties.center,
                    radiusMeters: radius.properties.radiusMeters,
                    hazardCount: operationalAwareness.properties.advisories.length,
                    timeTakenMs: afterTimestamp - beforeTimestamp
                }));
            });
        }
        catch (err:any){
            if (err?.name === 'AxiosError' && err.response?.status === 404) {
                // then probably outside the US, so lets do nothing
                // UI will show last authorized radius, but with opacity suggesting it is stale
                // and you won't be able to continue
            } else if (err?.message && err.message.startsWith('Unable to complete output ring')) {
                // we're in an area where turf fails, so set flag to show error message over flight checklist
                this.authorizeFailed = true;
                this.setState({ });
            } else {
                throw err;
            }
        }

    };

    getAuthorizedRadius = (): MissionFeatureCollection | undefined => {
        return this.radius?.properties.authorizedRadius;
    };

    remove = () => {
        this.panMovesRadius = false;
        this.radius = undefined;
        this.showPendingRadius = false;
        this.setState({ });

        // NOTE: we don't need to worry that we might be in the middle of authorizing, because that implementation
        // changes the radius it has clojure over, and won't re-set this.radius
    };

    adjustTime = async ({ startTime, endTime }: { startTime: ISO8601DateTimeString; endTime: ISO8601DateTimeString; }) => {
        this.buildAndSetRadiusFromCenter({
            ...this.radius!.properties,
            startTime,
            endTime
        });

        await this.authorizeRadius();
    };
}
